import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Legal } from '../api';
import { Logo } from '../ui';

export const LEGAL_URL = 'https://docs.justonair.com/legal';

/** Terms, privacy and the legal notice, under the sign-in card. */
export function LegalLinks() {
  return (
    <p className="legal-links">
      <a href={`${LEGAL_URL}/terms`} target="_blank" rel="noopener">Terms</a>
      <a href={`${LEGAL_URL}/privacy`} target="_blank" rel="noopener">Privacy</a>
      <a href={`${LEGAL_URL}/cookies`} target="_blank" rel="noopener">Cookies</a>
      <a href={`${LEGAL_URL}/notice`} target="_blank" rel="noopener">Legal notice</a>
    </p>
  );
}

/**
 * Shown instead of the dashboard until the account has accepted the current
 * Terms, AUP and DPA: accounts from before acceptance was recorded, and every
 * account again when the version changes. API keys keep working meanwhile.
 */
export function LegalGate({ legal, accountName, onSignOut }: { legal: Legal; accountName: string; onSignOut: () => void }) {
  const qc = useQueryClient();
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/v1/legal/accept', { method: 'POST', json: { version: legal.version } });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="center">
      <div className="auth" style={{ maxWidth: 460 }}>
        <div className="brand">
          <Logo />
        </div>
        <div className="card">
          <div>
            <h1>Our terms</h1>
            <p className="sub">To keep using the dashboard, accept the current terms for {accountName}.</p>
          </div>
          <label className="check legal-check">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>
              I accept the{' '}
              <a href={legal.documents.terms} target="_blank" rel="noopener">Terms of Service</a>,{' '}
              <a href={legal.documents.aup} target="_blank" rel="noopener">Acceptable Use Policy</a> and{' '}
              <a href={legal.documents.dpa} target="_blank" rel="noopener">Data Processing Agreement</a> on behalf of {accountName}.
            </span>
          </label>
          <p className="faint" style={{ margin: 0, fontSize: 13 }}>
            See our <a href={legal.privacy} target="_blank" rel="noopener">Privacy Policy</a>.
          </p>
          {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
          <button className="btn primary" type="button" disabled={!accepted || busy} onClick={accept} style={{ height: 40 }}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
          <button type="button" className="btn" onClick={onSignOut}>
            Sign out
          </button>
        </div>
        <LegalLinks />
      </div>
    </div>
  );
}
